import React, { useState } from 'react';
import { QrCode, Users, ExternalLink, CheckCircle, XCircle, Plus } from 'lucide-react';
import Popup from '../common/Popup';

const BlissaroPanel = ({ backendUrl }) => {
  const [clientEmail, setClientEmail] = useState('');
  const [clientData, setClientData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [checkResult, setCheckResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  
  // Stan dla popup
  const [popupConfig, setPopupConfig] = useState(null);
  const showPopup = (config) => setPopupConfig(config);
  const hidePopup = () => setPopupConfig(null);

  // Sprawdź czy klient ma konto w Blissaro
  const handleCheckUser = async () => {
    if (!clientEmail) {
      setError('Wprowadź adres email klienta');
      return;
    }

    setLoading(true);
    setError(null);
    setCheckResult(null);

    try {
      const response = await fetch(`${backendUrl}/admin/blissaro/check-user?email=${encodeURIComponent(clientEmail)}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Błąd sprawdzania konta');
      }
      
      const data = await response.json();
      setCheckResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Zarejestruj nowego klienta w Blissaro
  const handleRegisterUser = async () => {
    if (!clientData.name || !clientData.email || !clientData.password) {
      setError('Wypełnij wszystkie pola');
      return;
    }

    setRegistrationLoading(true);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/admin/blissaro/register-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(clientData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Błąd rejestracji');
      }
      
      const data = await response.json();
      setCheckResult({ exists: true, userId: data.user._id });
      setClientData({ name: '', email: '', password: '' });
      showPopup({
        title: 'Sukces',
        message: 'Klient został pomyślnie zarejestrowany w Blissaro!',
        type: 'success',
        size: 'compact'
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setRegistrationLoading(false);
    }
  };

  // Generuj przykładowy QR kod
  const handleGeneratePlaceholderQR = async () => {
    if (!checkResult?.exists) {
      setError('Klient musi mieć konto w Blissaro');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/admin/blissaro/generate-placeholder-qr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email: clientEmail }),
      });
      
      if (!response.ok) {
        throw new Error('Błąd generowania QR');
      }
      
      const data = await response.json();
      if (data.qrDownloadUrl) {
        window.open(data.qrDownloadUrl, '_blank');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>
          <QrCode size={24} style={{ marginRight: '10px' }} />
          Integracja Blissaro
        </h2>
      </div>

      <div className="panel-content">
        {/* Informacje o Blissaro */}
        <div className="info-section" style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
            <Users size={20} style={{ marginRight: '10px' }} />
            Co to jest Blissaro?
          </h3>
          <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
            Blissaro to aplikacja do generowania kodów QR dla gości weselnych, która umożliwia:
          </p>
          <ul style={{ marginLeft: '20px', lineHeight: '1.6' }}>
            <li>Automatyczne potwierdzanie obecności gości na weselu</li>
            <li>Zarządzanie listą gości z podziałem na grupy</li>
            <li>Generowanie unikalnych kodów QR dla każdej grupy</li>
            <li>Zbieranie potwierdzeń obecności w czasie rzeczywistym</li>
          </ul>
          <p style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
            <ExternalLink size={14} style={{ marginRight: '5px' }} />
            Więcej informacji: <a href="https://blissaro.pl" target="_blank" rel="noopener noreferrer">blissaro.pl</a>
          </p>
        </div>

        {/* Sprawdzenie konta klienta */}
        <div className="section" style={{ marginBottom: '30px' }}>
          <h3>Sprawdź konto klienta</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <input
              type="email"
              placeholder="Email klienta"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            <button
              onClick={handleCheckUser}
              disabled={loading}
              style={{ 
                padding: '10px 20px', 
                backgroundColor: '#007bff', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Sprawdzam...' : 'Sprawdź'}
            </button>
          </div>

          {checkResult && (
            <div style={{ 
              padding: '15px', 
              borderRadius: '4px', 
              backgroundColor: checkResult.exists ? '#d4edda' : '#f8d7da',
              border: `1px solid ${checkResult.exists ? '#c3e6cb' : '#f5c6cb'}`,
              display: 'flex',
              alignItems: 'center'
            }}>
              {checkResult.exists ? (
                <>
                  <CheckCircle size={20} color="#155724" style={{ marginRight: '10px' }} />
                  <span style={{ color: '#155724' }}>
                    Klient ma konto w Blissaro! (ID: {checkResult.userId})
                  </span>
                </>
              ) : (
                <>
                  <XCircle size={20} color="#721c24" style={{ marginRight: '10px' }} />
                  <span style={{ color: '#721c24' }}>
                    Klient nie ma konta w Blissaro
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Rejestracja nowego klienta */}
        {checkResult && !checkResult.exists && (
          <div className="section" style={{ marginBottom: '30px' }}>
            <h3>
              <Plus size={20} style={{ marginRight: '10px' }} />
              Utwórz konto dla klienta
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
              <input
                type="text"
                placeholder="Imię i nazwisko (np. Jan i Anna Kowalscy)"
                value={clientData.name}
                onChange={(e) => setClientData({...clientData, name: e.target.value})}
                style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <input
                type="email"
                placeholder="Email"
                value={clientData.email}
                onChange={(e) => setClientData({...clientData, email: e.target.value})}
                style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <input
                type="password"
                placeholder="Hasło (minimum 8 znaków)"
                value={clientData.password}
                onChange={(e) => setClientData({...clientData, password: e.target.value})}
                style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <button
              onClick={handleRegisterUser}
              disabled={registrationLoading}
              style={{ 
                padding: '10px 20px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: registrationLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {registrationLoading ? 'Rejestruję...' : 'Utwórz konto'}
            </button>
          </div>
        )}

        {/* Generowanie przykładowego QR */}
        {checkResult?.exists && (
          <div className="section">
            <h3>Przykładowy kod QR</h3>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              Wygeneruj przykładowy kod QR dla tego klienta (placeholder do testów):
            </p>
            <button
              onClick={handleGeneratePlaceholderQR}
              style={{ 
                padding: '10px 20px', 
                backgroundColor: '#17a2b8', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px'
              }}
            >
              <QrCode size={16} style={{ marginRight: '8px' }} />
              Wygeneruj przykładowy QR
            </button>
          </div>
        )}

        {/* Komunikaty błędów */}
        {error && (
          <div style={{ 
            marginTop: '20px',
            padding: '15px', 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb', 
            borderRadius: '4px',
            color: '#721c24'
          }}>
            {error}
          </div>
        )}
      </div>
      {popupConfig && (
        <Popup 
          config={popupConfig}
          onClose={hidePopup}
        />
      )}
    </div>
  );
};

export default BlissaroPanel; 